import {
  CdkDrag,
  CdkDragDrop,
  CdkDragPlaceholder,
  CdkDragPreview,
  CdkDropList
} from '@angular/cdk/drag-drop';
import {
  ChangeDetectionStrategy,
  Component,
  input,
  InputSignal,
  output,
  OutputEmitterRef
} from '@angular/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { TranslocoPipe } from '@jsverse/transloco';
import type { ActiveFilterChipEntry } from '../column-filter.utils';
import { DATA_GRID_GROUP_BAR_DROP_LIST_ID } from '../data-grid-grouping.utils';
import type { DataGridColumnDragData } from '../data-grid.types';

/**
 * Group-bar chrome: drop zone, group chips, filter chips, record count, and projected toolbar actions.
 */
@Component({
  selector: 'd-data-grid-group-bar',
  imports: [
    CdkDrag,
    CdkDropList,
    CdkDragPreview,
    CdkDragPlaceholder,
    MatCheckboxModule,
    MatIconModule,
    TranslocoPipe
  ],
  templateUrl: './data-grid-group-bar.component.html',
  styleUrl: './data-grid-group-bar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DataGridGroupBarComponent {
  /** Whether grouping chrome is enabled. */
  public groupingEnabled: InputSignal<boolean> = input<boolean>(true);

  /** Fields currently used for grouping, in order. */
  public groupByFields: InputSignal<Array<string>> = input<Array<string>>(new Array<string>());

  /** Thead drop-list ids connected to the group bar. */
  public theadDropListIds: InputSignal<Array<string>> = input<Array<string>>(new Array<string>());

  /** Field → header label map for group chips. */
  public columnHeaders: InputSignal<Map<string, string>> = input<Map<string, string>>(
    new Map<string, string>()
  );

  /** CDK enter predicate for the group bar drop list. */
  public canEnterGroupBar: InputSignal<(drag: CdkDrag<DataGridColumnDragData>) => boolean> = input<
    (drag: CdkDrag<DataGridColumnDragData>) => boolean
  >((drag: CdkDrag<DataGridColumnDragData>): boolean => drag.data.isShared);

  /** Active filter chips. */
  public activeFilterEntries: InputSignal<Array<ActiveFilterChipEntry>> = input<Array<ActiveFilterChipEntry>>(
    new Array<ActiveFilterChipEntry>()
  );

  /** Server-side total record count. */
  public totalCount: InputSignal<number> = input<number>(0);

  /** Whether the grouped select-all checkbox should render beside the record count. */
  public showSelectAllCheckbox: InputSignal<boolean> = input<boolean>(false);

  /** Whether every matching row is currently selected. */
  public selectAllChecked: InputSignal<boolean> = input<boolean>(false);

  /** Whether a partial selection is active. */
  public selectAllIndeterminate: InputSignal<boolean> = input<boolean>(false);

  /** Emits when a column is dropped on the group bar. */
  public columnDropped: OutputEmitterRef<CdkDragDrop<Array<string>, Array<string>, DataGridColumnDragData>> =
    output<CdkDragDrop<Array<string>, Array<string>, DataGridColumnDragData>>();

  /** Emits the field to remove from the group chain. */
  public removeGroupField: OutputEmitterRef<string> = output<string>();

  /** Emits when the user clears all grouping. */
  public clearAllGroups: OutputEmitterRef<void> = output<void>();

  /** Emits the filter field to clear. */
  public removeFilter: OutputEmitterRef<string> = output<string>();

  /** Emits when the grouped select-all checkbox is toggled. */
  public toggleSelectAll: OutputEmitterRef<void> = output<void>();

  /** Stable CDK drop-list id shared with leaf theads. */
  protected readonly groupBarDropListId: string = DATA_GRID_GROUP_BAR_DROP_LIST_ID;

  /**
   * Looks up the display header for a grouped field.
   *
   * @param field - Column field name.
   * @returns Header label, or the raw field when unknown.
   */
  protected headerFor(field: string): string {
    return this.columnHeaders().get(field) ?? field;
  }
}
